import React, { useEffect, useMemo, useState } from "react";
import styled, { withTheme } from "styled-components";
import moment from "moment";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import NutritionItem from "../NutritionItem/NutritionItem";
import PieChart from "../../ui-library/PieChart/PieChart";
import {
  dailyTargetIds,
  getExtendedDateFormat,
  getMacronutrientColor,
  UNIT,
} from "../../../modules/constants";
import {
  addOtherSlice,
  decimalPlaces,
  roundMealPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { useTranslation } from "react-i18next";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  p {
    margin-bottom: 20px;
    & + div {
      margin-bottom: 0px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledPieChart = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const DailySummary = (props) => {
  const {
    completeDayData,
    finishDay,
    closeModal,
    dataStats,
    theme,
    isFastingDayConfirmation,
    isModal,
  } = props;

  const { t } = useTranslation();

  const [pieChartData, setPieChartData] = useState(null);

  const caloriesItem = useMemo(
    () => dataStats.find((item) => item.id === dailyTargetIds.CALORIES_ATTR_ID),
    [dataStats]
  );

  useEffect(() => {
    if (dataStats && !pieChartData) {
      // summing data for charts from day items

      const longChainFatsItem = dataStats.find(
        (item) => item.id === dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID
      );
      const mediumChainFatsItem = dataStats.find(
        (item) => item.id === dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID
      );
      const proteinItem = dataStats.find(
        (item) => item.id === dailyTargetIds.PROTEIN_ATTR_ID
      );
      const carbsItem = dataStats.find(
        (item) => item.id === dailyTargetIds.CARBS_ATTR_ID
      );

      const lcfFrequency = roundMealPercentage(
        roundMealValue(longChainFatsItem?.frequency ?? 0)
      );

      const lcfPercentage = `${Number(
        roundMealPercentage(roundMealValue(longChainFatsItem?.frequency ?? 0))
      ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE}`;

      const longChainFatsPie = {
        letter: t("appText.dietDiary.lcf"),
        frequency: lcfFrequency,
        color: getMacronutrientColor(
          dailyTargetIds.LONG_CHAIN_FATS_ATTR_ID,
          theme
        ),
        percentage: lcfPercentage,
        info: `${lcfPercentage} - ${Number(
          roundMealValue(longChainFatsItem?.count ?? 0).toFixed(decimalPlaces)
        )}g`,
        isVisible: true,
      };

      const mcfFrequency = roundMealPercentage(
        roundMealValue(mediumChainFatsItem?.frequency ?? 0)
      );

      const mcfPercentage = `${Number(
        roundMealPercentage(roundMealValue(mediumChainFatsItem?.frequency ?? 0))
      ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE}`;

      const mediumChainFatsPie = {
        letter: t("appText.dietDiary.mcf"),
        frequency: mcfFrequency,
        color: getMacronutrientColor(
          dailyTargetIds.MEDIUM_CHAIN_FATS_ATTR_ID,
          theme
        ),
        percentage: mcfPercentage,
        info: `${mcfPercentage} - ${Number(
          roundMealValue(mediumChainFatsItem?.count ?? 0).toFixed(decimalPlaces)
        )}g`,
        isVisible: true,
      };

      const proteinFrequency = roundMealPercentage(
        roundMealValue(proteinItem?.frequency ?? 0)
      );

      const proteinPercentage = `${Number(
        roundMealPercentage(roundMealValue(proteinItem?.frequency ?? 0))
      ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE}`;

      const proteinPie = {
        letter: t("appText.dietDiary.protein"),
        frequency: proteinFrequency,
        color: getMacronutrientColor(dailyTargetIds.PROTEIN_ATTR_ID, theme),
        percentage: proteinPercentage,
        info: `${proteinPercentage} - ${Number(
          roundMealValue(proteinItem?.count ?? 0)
        ).toFixed(decimalPlaces)}g`,
        isVisible: true,
      };

      const carbsFrequency = roundMealPercentage(
        roundMealValue(proteinItem?.frequency ?? 0)
      );

      const carbsPercentage = `${Number(
        roundMealPercentage(roundMealValue(proteinItem?.frequency ?? 0))
      ).toFixed(decimalPlaces)}${UNIT.PERCENTAGE}`;

      const carbsPie = {
        letter: t("appText.dietDiary.carbs"),
        frequency: carbsFrequency,
        color: getMacronutrientColor(dailyTargetIds.CARBS_ATTR_ID, theme),
        percentage: carbsPercentage,
        info: `${carbsPercentage} - ${Number(
          roundMealValue(carbsItem?.count ?? 0)
        ).toFixed(decimalPlaces)}g`,
        isVisible: true,
      };

      const data = [longChainFatsPie, mediumChainFatsPie, proteinPie, carbsPie];

      data.push(addOtherSlice(data, theme, t));

      setPieChartData(data);
    }
  }, [dataStats, pieChartData, setPieChartData, theme, t]);

  return (
    <ComponentWrapper data-testid="day-summary">
      <p>
        <span
          dangerouslySetInnerHTML={{
            __html: t(
              isFastingDayConfirmation
                ? "appText.dietDiary.completeDay.message"
                : "appText.dietDiary.completeDay.withMeals.message",
              {
                date: `<b>${moment(completeDayData.day).format(
                  getExtendedDateFormat()
                )}</b>`,
              }
            ),
          }}
        />
      </p>
      <NutritionItem
        value={`${roundMealValue(caloriesItem?.count ?? 0).toFixed(
          decimalPlaces
        )}kcal`}
        isModal={isModal}
        isFastingDay={isFastingDayConfirmation}
        backgroundColor={props.theme?.lightShadeMagenta}
        titleColor={props.theme?.darkPurple}
      />

      {pieChartData && (
        <StyledPieChart>
          <PieChart series={pieChartData} width={380} height={300} />
        </StyledPieChart>
      )}

      {isFastingDayConfirmation ? (
        <AlertUGNX type={AlertType.WARNING}>
          {t("appText.dietDiary.completeDayFasting.confirmation")}
        </AlertUGNX>
      ) : (
        <p>{t("appText.dietDiary.completeDay.withMeals.warning")}</p>
      )}
      <ActionButtonsContainer>
        <SecondaryButton
          data-testid="day-summary-cancel-button"
          className="me-2"
          onClick={closeModal}
        >
          {t("appText.cancel")}
        </SecondaryButton>
        <PrimaryButton
          data-testid="day-summary-confirm-button"
          onClick={finishDay}
        >
          {t("appText.confirm")}
        </PrimaryButton>
      </ActionButtonsContainer>
    </ComponentWrapper>
  );
};
export default withTheme(DailySummary);
